import { useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { SecretField } from '@/shared/ui/Field';

import { useResetPassword } from '../api/passwordReset';
import {
  MIN_PASSWORD_LENGTH,
  isSubmittable,
  passwordProblemKey,
  resetRefusal,
  tokenFromQuery,
} from '../model/reset';

/* Spending a reset token: a new password, twice, and one of four things the screen can be saying.
 *
 * **A link with no token is its own state, and it renders no form at all.** The token is read from
 * `?token=…`; when there is none the link was mangled on the way here — truncated by a mail client,
 * copied without its query string — and offering two password boxes that cannot possibly work is
 * worse than saying so. It is deliberately *not* the same message as a rejected token: that one means
 * the platform read a token and refused it, which is nearly always expiry, and telling somebody their
 * link expired when it never arrived intact sends them to wait for a second mail that will fail
 * identically. `tokenFromQuery` and `resetRefusal` keep the two apart and are tested for it.
 *
 * **Two fields rather than one with a reveal.** `SecretField` is uncontrolled by design, so there is
 * no value to reveal — and a typo in a password nobody can read is exactly what a confirmation box
 * exists to catch, on the one form where getting it wrong locks the reader out of the account they
 * are trying to recover.
 *
 * **The strength rule is mirrored in the hint rather than discovered after a failed submit**, the
 * same way `SignupForm` mirrors it, and from the same constant so the two screens cannot state
 * different numbers.
 *
 * `onDone` rather than a redirect here: the screen decides where "done" goes, matching
 * `LoginForm.onLoggedIn` and `SignupForm.onSignedUp`. What it carries is stated at the call site in
 * `App.tsx` — a completed reset does *not* sign anybody in, so the reader lands on `/login` and uses
 * the password they just set, which is the one action that proves it took effect.
 */
export function ResetPasswordForm({
  onDone,
}: {
  readonly onDone: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { search } = useLocation();
  const reset = useResetPassword();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const parsed = tokenFromQuery(search);
  const problemKey = passwordProblemKey(password, confirmation);

  if (parsed.kind === 'missing') {
    return (
      <>
        <h1 className="lq-auth__heading">{t('auth.reset.title')}</h1>
        {/* No form beneath it. There is nothing this screen could do with what a reader typed. */}
        <Alert tone="warning" title={t('auth.reset.noTokenTitle')}>
          {t('auth.reset.noTokenBody')}
        </Alert>
        <p className="lq-dim" style={{ marginTop: 'var(--space-4)' }}>
          <Link to={SCREEN_PATHS.forgotPassword}>{t('auth.reset.requestAnother')}</Link>
        </p>
      </>
    );
  }

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setAttempted(true);
    if (!isSubmittable(password, confirmation)) return;
    reset.mutate(
      { token: parsed.token, password },
      {
        onSuccess: () => {
          onDone();
        },
        onError: () => {
          /* Both boxes are cleared on a refusal, matching `LoginForm`: a password does not survive a
           * failed attempt in memory longer than it has to, so a screenshot or a crash report taken
           * after the error does not carry it. Clearing both also stops a reader retrying a pair the
           * server has already refused on strength grounds. */
          setPassword('');
          setConfirmation('');
          setResetKey((generation) => generation + 1);
        },
      },
    );
  };

  /* The error is held in a local rather than read back off the mutation at each branch: `isError` and
   * `error` are separate fields on the result, so the type checker cannot know the second is non-null
   * because the first is true — and narrowing it once here is what lets the server's own sentence be
   * rendered without a non-null assertion. */
  const failure = reset.isError ? reset.error : null;
  const refusal = failure === null ? null : resetRefusal(failure);

  return (
    <>
      <h1 className="lq-auth__heading">{t('auth.reset.title')}</h1>
      <p className="lq-form-note">{t('auth.reset.lede')}</p>

      <form onSubmit={onSubmit}>
        <SecretField
          label={t('auth.reset.newPassword')}
          onChange={setPassword}
          hint={t('auth.signup.passwordHint', { minLength: MIN_PASSWORD_LENGTH })}
          required
          resetKey={resetKey}
        />
        <SecretField
          label={t('auth.reset.confirmPassword')}
          onChange={setConfirmation}
          required
          resetKey={resetKey}
        />

        {/* Shown once the pair has been touched, never on a pristine form — the rule the rule-
            authoring fields already hold: a form that is red before it has been filled in teaches a
            reader to ignore red. `attempted` covers the case of submitting an empty form. */}
        {/* `minLength` is passed for whichever key comes back, and the one that does not take it
            ignores it. The alternative — a model that returned a key *and* its arguments — would put
            the length rule in two shapes for one message, and a `t(key)` with no arguments would
            render `{minLength}` on screen, which is the literal-interpolation defect this platform
            has shipped twice. */}
        {problemKey !== null && (
          <Alert tone="warning">
            {t(problemKey, { minLength: MIN_PASSWORD_LENGTH })}
          </Alert>
        )}
        {attempted && problemKey === null && !isSubmittable(password, confirmation) && (
          <Alert tone="warning">{t('auth.reset.incomplete')}</Alert>
        )}

        {/* The three refusals nested under one `failure !== null`, which is also what narrows it for
            the two branches that render the server's own sentence. Three sibling checks on `refusal`
            would each need their own non-null test — and a non-null assertion instead would be the
            one place a genuinely absent error rendered as a crash. */}
        {failure !== null && (
          <>
            {refusal === 'token' && (
              /* One indistinguishable answer for unknown, expired and already-spent, matching the
                 server. The next step is a fresh link, so the link to ask for one is in the alert
                 rather than below the fold. */
              <Alert tone="warning" title={t('auth.reset.tokenRejectedTitle')}>
                {t('auth.reset.tokenRejectedBody')}{' '}
                <Link to={SCREEN_PATHS.forgotPassword}>
                  {t('auth.reset.requestAnother')}
                </Link>
              </Alert>
            )}

            {refusal === 'password' && (
              /* The server's own sentence, verbatim. It names the rule that was failed, and
                 summarising it here would make this screen the place that summary drifts from the
                 domain. */
              <Alert tone="warning" title={t('auth.reset.weakTitle')}>
                {failure.message}
              </Alert>
            )}

            {refusal === 'other' && (
              <Alert tone="error" title={t('auth.reset.failedTitle')}>
                {failure.message}
              </Alert>
            )}
          </>
        )}

        <Button type="submit" tone="primary" size="block" busy={reset.isPending}>
          {t('auth.reset.submit')}
        </Button>
      </form>

      <p className="lq-dim" style={{ marginTop: 'var(--space-4)' }}>
        <Link to={SCREEN_PATHS.login}>{t('auth.forgot.backToLogin')}</Link>
      </p>
    </>
  );
}
